import { useState, useEffect, useCallback } from "react";
import type { OpsMateCustomer, ViewName } from "../types";
import { lookupCustomerByEmail } from "../services/api";
import { extractEmailFromConversation } from "../services/missive";
import { useAuth } from "./useAuth";

interface CustomerLookupState {
  view: ViewName;
  customer: OpsMateCustomer | null;
  email: string;
  errorMessage: string;
  emptyMessage: string;
}

export function useCustomerLookup(conversationIds: string[]) {
  const { token, refreshToken } = useAuth();

  const [state, setState] = useState<CustomerLookupState>({
    view: "empty",
    customer: null,
    email: "",
    errorMessage: "",
    emptyMessage: "Select a conversation to view customer",
  });

  const lookupByEmail = useCallback(
    async (email: string) => {
      if (!token) {
        setState((s) => ({ ...s, view: "login" }));
        return;
      }

      setState((s) => ({ ...s, view: "loading" }));

      try {
        const result = await lookupCustomerByEmail(
          email,
          token,
          refreshToken
        );

        if (!result || !result.authUser?.uid) {
          setState((s) => ({
            ...s,
            view: "notFound",
            email,
            customer: null,
          }));
          return;
        }

        setState((s) => ({
          ...s,
          view: "customer",
          customer: result,
          email,
        }));
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message === "Authentication required" ||
            error.message === "Session expired")
        ) {
          return;
        }

        setState((s) => ({
          ...s,
          view: "error",
          errorMessage: "Error loading customer data",
        }));
      }
    },
    [token, refreshToken]
  );

  const retry = useCallback(() => {
    if (state.email) {
      lookupByEmail(state.email);
    }
  }, [state.email, lookupByEmail]);

  const searchByEmail = useCallback(
    (email: string) => {
      if (email.trim()) {
        lookupByEmail(email.trim());
      }
    },
    [lookupByEmail]
  );

  const showSearch = useCallback((email = "") => {
    setState((s) => ({
      ...s,
      view: "notFound",
      email,
      customer: null,
    }));
  }, []);

  useEffect(() => {
    if (!token) return;

    if (!conversationIds || conversationIds.length === 0) {
      setState((s) => ({
        ...s,
        view: "empty",
        emptyMessage: "Select a conversation to view customer",
        customer: null,
      }));
      return;
    }

    if (conversationIds.length > 1) {
      setState((s) => ({
        ...s,
        view: "empty",
        emptyMessage: "Select a single conversation",
        customer: null,
      }));
      return;
    }

    setState((s) => ({ ...s, view: "loading" }));

    (async () => {
      try {
        if (typeof Missive === "undefined") {
          throw new Error("Missive SDK not available");
        }

        const conversations = await Missive.fetchConversations(conversationIds);

        if (!conversations || conversations.length === 0) {
          setState((s) => ({
            ...s,
            view: "empty",
            emptyMessage: "Could not load conversation",
          }));
          return;
        }

        const email = extractEmailFromConversation(conversations[0]);

        if (!email) {
          setState((s) => ({
            ...s,
            view: "empty",
            emptyMessage: "No email found in conversation",
          }));
          return;
        }

        await lookupByEmail(email);
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message === "Authentication required" ||
            error.message === "Session expired")
        ) {
          return;
        }

        console.error("Error fetching customer:", error);
        setState((s) => ({
          ...s,
          view: "error",
          errorMessage: "Error loading customer data",
        }));
      }
    })();
  }, [conversationIds, token, lookupByEmail]);

  return {
    ...state,
    retry,
    searchByEmail,
    showSearch,
  };
}
